import React, { useCallback, useEffect, useRef, useState } from 'react';
import Icon from '@mdi/react';
import { mdiTruckDeliveryOutline, mdiAlertOutline, mdiOpenInNew } from '@mdi/js';
import global from '../global';

const FETCH_INTERVAL_MS = 2 * 60 * 1000; // 2 minute, kao i obavijesti uprave
const SHOWN_KEY = 'transfersAlertShownAt';

/**
 * Nalozi za slanje na blagajni: traka dok postoji pošiljka za zaprimiti ili
 * nalog za poslati, skočni prozor pri prijavi ("Imate X pošiljki za zaprimiti /
 * Y naloga za poslati"), a nakon roka se prozor ne može zatvoriti bez
 * zaprimanja ili upisa razloga kašnjenja. Zaprimanje ide u admin (skeniranje
 * kao i dosad) kroz gumb "Zaprimi".
 */
const TransfersAlert = () => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const openedForcedRef = useRef(false);

  const fetchData = useCallback(async () => {
    try {
      if (!global?.invoiceDevice?.id) return;

      const res = await window.axios.get('/transfers/pending');
      const d = res?.data || null;
      setData(d);

      if (!d || d.enabled === false) return;

      const incoming = d.incoming || [];
      const outgoing = d.outgoing || [];
      const total = incoming.length + outgoing.length + (d.reservations || 0);
      const forced = incoming.some((p) => p.force_reason);

      // Nakon roka prozor se otvara sam dok se ne zaprimi ili ne upiše razlog
      if (forced && !openedForcedRef.current) {
        openedForcedRef.current = true;
        setOpen(true);
        return;
      }

      // Pri prijavi (jednom po sesiji blagajne) ako ima što za napraviti
      if (total > 0 && !sessionStorage.getItem(SHOWN_KEY)) {
        sessionStorage.setItem(SHOWN_KEY, String(Date.now()));
        setOpen(true);
      }
    } catch (err) {
      console.warn('Greška kod dohvaćanja naloga za slanje:', err?.response?.data || err.message);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const t = setInterval(fetchData, FETCH_INTERVAL_MS);
    return () => clearInterval(t);
  }, [fetchData]);

  if (!data || data.enabled === false) return null;

  const incoming = data.incoming || [];
  const outgoing = data.outgoing || [];
  const reservations = data.reservations || 0;
  const blocked = data.blocked || [];
  const forced = incoming.filter((p) => p.force_reason);
  const mustReason = forced.length > 0;
  const anyOverdue = incoming.some((p) => p.overdue) || outgoing.some((p) => p.overdue);
  const total = incoming.length + outgoing.length + reservations;

  if (total === 0 && blocked.length === 0) return null;

  const close = () => {
    if (mustReason) {
      setError('Pošiljka je prešla rok: zaprimite je ili upišite razlog kašnjenja.');
      return;
    }
    setError('');
    setOpen(false);
  };

  const submitReason = async () => {
    if (reason.trim().length < 3) {
      setError('Razlog mora imati bar 3 znaka.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      for (const p of forced) {
        await window.axios.post(`/transfers/${p.id}/delay-reason`, { reason: reason.trim() });
      }
      setReason('');
      await fetchData();
      setOpen(false);
    } catch (err) {
      setError(err?.response?.data?.message || 'Spremanje razloga nije uspjelo.');
    } finally {
      setSaving(false);
    }
  };

  const summaryParts = [];
  if (incoming.length) summaryParts.push(`${incoming.length} ${incoming.length === 1 ? 'pošiljka' : 'pošiljki'} za zaprimiti`);
  if (outgoing.length) summaryParts.push(`${outgoing.length} ${outgoing.length === 1 ? 'nalog' : 'naloga'} za poslati`);
  if (reservations) summaryParts.push(`${reservations} ${reservations === 1 ? 'rezervacija' : 'rezervacija'} za slanje`);

  return (
    <>
      <div className="pl-2 pr-4 mt-0 bg-blue-gray-50 pt-1">
        <div className={`rounded-sm text-white shadow mt-1 ${anyOverdue || mustReason ? 'bg-red-700' : 'bg-amber-600'}`}>
          <div className="mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex h-11 items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Icon path={mustReason ? mdiAlertOutline : mdiTruckDeliveryOutline} size={0.8} className="text-white flex-shrink-0" />
                <div className="text-[13px] font-semibold truncate">
                  {summaryParts.join(' · ')}
                  {blocked.length > 0 && <span className="font-normal"> · blokada novih naloga ({blocked.length})</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(true)}
                className="flex-shrink-0 text-[12px] uppercase font-semibold bg-white/20 hover:bg-white/30 rounded px-3 py-1"
              >
                Prikaži
              </button>
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className={`px-5 py-3 rounded-t-lg text-white flex items-center justify-between ${mustReason ? 'bg-red-700' : 'bg-amber-600'}`}>
              <div className="font-semibold text-base">
                Imate {incoming.length} {incoming.length === 1 ? 'pošiljku' : 'pošiljki'} za zaprimiti / {outgoing.length} {outgoing.length === 1 ? 'nalog' : 'naloga'} za poslati
              </div>
              {!mustReason && (
                <button type="button" onClick={close} className="text-white/90 hover:text-white text-xl leading-none" aria-label="Zatvori">×</button>
              )}
            </div>

            <div className="p-5 overflow-y-auto space-y-5">
              {blocked.length > 0 && (
                <div className="rounded border border-red-300 bg-red-50 text-red-800 p-3 text-sm">
                  <strong>Blokada:</strong> dok ne zaprimite
                  {' '}{blocked.map((b) => `${b.number} iz ${b.from} (${b.days} d)`).join(', ')}
                  , ne možete otvoriti novi nalog prema tom skladištu.
                </div>
              )}

              {incoming.length > 0 && (
                <div>
                  <div className="font-semibold mb-2">Za zaprimiti</div>
                  <div className="divide-y border rounded">
                    {incoming.map((p) => (
                      <div key={`in-${p.id}`} className={`flex items-center justify-between gap-3 px-3 py-2 ${p.overdue ? 'bg-red-50' : ''}`}>
                        <div className="text-sm min-w-0">
                          <div className="font-semibold truncate">{p.number} iz {p.from}</div>
                          <div className="text-gray-600">
                            {p.items} st. · {Math.round(p.quantity)} kom · poslano {p.sent_at}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs font-semibold rounded px-2 py-1 ${p.overdue ? 'bg-red-600 text-white' : 'bg-gray-200 text-gray-800'}`}>
                            {p.days} d{p.force_reason ? ' · prošao rok' : ''}
                          </span>
                          <a
                            href={p.receive_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1 text-xs font-semibold uppercase bg-green-600 hover:bg-green-700 text-white rounded px-3 py-1"
                          >
                            Zaprimi <Icon path={mdiOpenInNew} size={0.6} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {outgoing.length > 0 && (
                <div>
                  <div className="font-semibold mb-2">Za poslati</div>
                  <div className="divide-y border rounded">
                    {outgoing.map((p) => (
                      <div key={`out-${p.id}`} className={`flex items-center justify-between gap-3 px-3 py-2 ${p.overdue ? 'bg-amber-50' : ''}`}>
                        <div className="text-sm min-w-0">
                          <div className="font-semibold truncate">{p.number} za {p.to}</div>
                          <div className="text-gray-600">{p.status_label} · {p.items} st.</div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs font-semibold rounded px-2 py-1 ${p.overdue ? 'bg-amber-600 text-white' : 'bg-gray-200 text-gray-800'}`}>{p.days} d</span>
                          <a href={p.edit_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold uppercase bg-blue-600 hover:bg-blue-700 text-white rounded px-3 py-1">
                            Otvori <Icon path={mdiOpenInNew} size={0.6} />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {reservations > 0 && (
                <div className="flex items-center justify-between rounded border px-3 py-2">
                  <div className="text-sm"><span className="font-semibold">{reservations}</span> {reservations === 1 ? 'rezervacija čeka' : 'rezervacija čekaju'} slanje</div>
                  <a href={data.reservations_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold uppercase bg-amber-600 hover:bg-amber-700 text-white rounded px-3 py-1">
                    Pošalji rezervacije <Icon path={mdiOpenInNew} size={0.6} />
                  </a>
                </div>
              )}

              {mustReason && (
                <div className="rounded border border-red-300 p-3">
                  <div className="font-semibold text-red-700 mb-1">
                    {forced.length === 1 ? 'Pošiljka je prešla rok zaprimanja.' : `${forced.length} pošiljke su prešle rok zaprimanja.`}
                  </div>
                  <div className="text-sm text-gray-700 mb-2">Zaprimite je gumbom „Zaprimi" ili upišite razlog kašnjenja; razlog vidi voditelj.</div>
                  <textarea
                    className="w-full border rounded p-2 text-sm"
                    rows={2}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Npr. pošiljka još nije fizički stigla, dostava kasni"
                  />
                </div>
              )}

              {error && <div className="text-sm text-red-700">{error}</div>}
            </div>

            <div className="px-5 py-3 border-t flex items-center justify-end gap-2">
              {mustReason ? (
                <>
                  <button type="button" onClick={fetchData} className="text-sm px-3 py-2 rounded border hover:bg-gray-50">Osvježi (zaprimio sam)</button>
                  <button type="button" onClick={submitReason} disabled={saving} className="text-sm px-4 py-2 rounded bg-red-700 hover:bg-red-800 text-white font-semibold disabled:opacity-60">
                    {saving ? 'Spremam…' : 'Spremi razlog i zatvori'}
                  </button>
                </>
              ) : (
                <button type="button" onClick={close} className="text-sm px-4 py-2 rounded bg-gray-800 hover:bg-gray-900 text-white font-semibold">U redu</button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default TransfersAlert;
